import React, { useEffect, useRef } from 'react';
import { Animated, TouchableOpacity, View } from 'react-native';
import { createStyles } from '@/contexts/theme-context';
import { shadow } from '@/styles';

interface SegmentedControlOption<Option extends string> {
  accessibilityLabel: string;
  children: React.ReactNode;
  key: Option;
  onPress: () => void;
}

interface SegmentedControlProps<Option extends string> {
  options: [SegmentedControlOption<Option>, SegmentedControlOption<Option>];
  selectedOption: Option;
}

export const SegmentedControl = <Option extends string>({
  options,
  selectedOption,
}: SegmentedControlProps<Option>) => {
  const [styles] = useStyles();

  // segmented control animation
  const animatedValue = useRef(
    new Animated.Value(selectedOption === options[0].key ? 0 : 1),
  ).current;
  const isInitialMount = useRef(true);

  useEffect(() => {
    if (options.length < 2) {
      return;
    }

    // skip animation on initial mount
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    Animated.timing(animatedValue, {
      duration: 300,
      toValue: selectedOption === options[0].key ? 0 : 1,
      useNativeDriver: false,
    }).start();
  }, [animatedValue, selectedOption, options]);
  const translateX = animatedValue.interpolate({
    inputRange: [0, 1],
    outputRange: ['0%', '100%'],
  });

  if (options.length < 2) {
    console.warn('SegmentedControl must have at least 2 options');
    return null;
  }

  // support 2 way segmented control for now
  return (
    <View style={styles.container}>
      <Animated.View
        style={[
          styles.outerButton,
          {
            transform: [{ translateX }],
          },
        ]}
      />
      <TouchableOpacity
        accessibilityLabel={options[0].accessibilityLabel}
        accessibilityRole="button"
        activeOpacity={1}
        aria-current={options[0].key === selectedOption}
        onPress={options[0].onPress}
        style={styles.baseButton}
      >
        {options[0].children}
      </TouchableOpacity>
      <TouchableOpacity
        accessibilityLabel={options[1].accessibilityLabel}
        accessibilityRole="button"
        activeOpacity={1}
        aria-current={options[1].key === selectedOption}
        onPress={options[1].onPress}
        style={styles.baseButton}
      >
        {options[1].children}
      </TouchableOpacity>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  baseButton: {
    alignItems: 'center',
    borderRadius: 24,
    display: 'flex',
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'center',
  },
  container: {
    flex: 1,
    flexDirection: 'row',
    height: 32,
  },
  outerButton: {
    backgroundColor: theme.sk.color.bg.primary.default,
    borderRadius: 24,
    ...shadow({
      color: theme.colors.black,
      elevation: 1,
      opacity: 0.1,
      radius: theme.sk.radii.radiusMedium,
      x: 0,
      y: 4,
    }),
    height: 32,
    left: 0,
    position: 'absolute',
    top: 0,
    width: '50%',
  },
}));
